import { __rest } from '../../node_modules/tslib/tslib.es6.js';
import CloseIcon from '@mui/icons-material/Close';
import { Dialog, Slide, useMediaQuery, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion, useDragControls } from 'framer-motion';
import React__default, { useRef } from 'react';

// Styled Dialog component
const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        backgroundColor: theme.vars.palette.background.paper,
        boxShadow: '0 5px 10px 0 rgba(181, 176, 176, 0.16)',
        paddingTop: '24px',
        transition: 'all 0.3s ease-in-out',
    },
    '&.MuiDialog-root': {
        alignItems: 'center',
        justifyContent: 'center', // Center horizontally
    },
    // Mobile styles
    [theme.breakpoints.down('md')]: {
        '& .MuiDialog-paper': {
            position: 'absolute',
            backgroundColor: theme.vars.palette.background.paper,
            bottom: 0,
            left: 0,
            right: 0,
            margin: 0,
            boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.1)',
            paddingTop: '12px', // Add padding for the handle
        },
        '&.MuiDialog-root': {
            alignItems: 'flex-end',
            width: '100%',
        },
    },
}));
// Drag handle for bottom sheet (mobile only)
const DragHandle = styled('div')(() => ({
    width: '40px',
    height: '5px',
    backgroundColor: '#CCCCCC',
    borderRadius: '4px',
    margin: '0 auto',
    position: 'absolute',
    top: '8px',
    left: 'calc(50% - 20px)',
    zIndex: 10, // Ensure it's above other content
}));
// Slide transition for mobile (slides up from bottom)
const SlideUpTransition = React__default.forwardRef(function Transition(props, ref) {
    return React__default.createElement(Slide, Object.assign({ direction: "up", ref: ref }, props));
});
const StyledDialogPaper = styled(motion.div)(({ theme }) => ({
    width: 'fit-content',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    borderTopRightRadius: '10px',
    borderTopLeftRadius: '10px',
    borderBottomRightRadius: '10px',
    borderBottomLeftRadius: '10px',
    [theme.breakpoints.down('md')]: {
        width: '100%',
        maxWidth: '100%',
        borderTopRightRadius: '8px',
        borderTopLeftRadius: '8px',
        borderBottomRightRadius: '0px',
        borderBottomLeftRadius: '0px',
    },
}));
// Wrap Dialog Paper in motion component
const MotionDialogPaper = React__default.forwardRef(function DialogPaper(props, ref) {
    return (React__default.createElement(StyledDialogPaper, Object.assign({ ref: ref, drag: props.drag, dragControls: props.dragControls, dragConstraints: props.dragConstraints, dragElastic: 0.2, dragTransition: {
            bounceStiffness: 300,
            bounceDamping: 30,
            timeConstant: 400,
        }, onDragEnd: props.onDragEnd }, props)));
});
/**
 * ResponsiveDialog - A dialog component that behaves as a modal on desktop
 * and as a bottom sheet on mobile devices with swipe-to-dismiss functionality
 */
const ResponsiveDialog = (_a) => {
    var { open, onClose, children } = _a, dialogProps = __rest(_a, ["open", "onClose", "children"]);
    // Check if we're on mobile
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
    // Choose the appropriate transition based on screen size
    const TransitionComponent = isMobile ? SlideUpTransition : undefined;
    // Create drag controls for the mobile bottom sheet
    const dragControls = useDragControls();
    // Ref for motion constraints
    const constraintsRef = useRef(null);
    // Start drag when user interacts with the handle
    const startDrag = (event) => {
        if (!isMobile)
            return;
        dragControls.start(event);
    };
    // The full Dialog component with swipe-to-close functionality
    return (React__default.createElement(StyledDialog, Object.assign({ open: open, onClose: onClose, TransitionComponent: TransitionComponent, PaperComponent: MotionDialogPaper, PaperProps: Object.assign({}, (isMobile && {
            drag: 'y',
            dragControls: dragControls,
            dragConstraints: { top: 0, bottom: 100 },
            dragElastic: 0.2,
            dragTransition: {
                bounceStiffness: 600,
                bounceDamping: 30,
                timeConstant: 300,
            },
            dragSnapToOrigin: true,
            onDragEnd: (event, info) => {
                // Close the dialog only if dragged down far enough or with enough velocity
                if (info.offset.y > 150 || info.velocity.y > 300) {
                    onClose === null || onClose === void 0 ? void 0 : onClose();
                }
                // Otherwise, it will automatically snap back to origin due to dragSnapToOrigin: true
            },
            ref: constraintsRef,
        })) }, dialogProps),
        isMobile ? (React__default.createElement("div", { className: "bc_dialog_drag_handle", style: {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                marginTop: '8px',
                height: '40px',
                touchAction: 'none',
                zIndex: 1,
                cursor: 'grab',
            }, onPointerDown: startDrag },
            React__default.createElement(DragHandle, null))) : onClose ? (React__default.createElement("div", { className: "bc_dialog_close_button", style: {
                position: 'absolute',
                top: 15,
                right: 14,
                height: '40px',
                width: '40px',
                touchAction: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
            }, onPointerDown: startDrag },
            React__default.createElement(IconButton, { "aria-label": "close", onClick: onClose, disableRipple: true, sx: {
                    all: 'unset',
                    width: '24px',
                    height: '24px',
                    '&:hover': {
                        backgroundColor: 'transparent',
                    },
                } },
                React__default.createElement(CloseIcon, { sx: (theme) => ({ color: theme.vars.palette.text.primary }) })))) : null,
        children));
};

export { ResponsiveDialog };
//# sourceMappingURL=ResponsiveDialog.js.map
